import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, CircleNotch, Star, PaperPlaneTilt, Image, Eye, ShareNetwork,
  GoogleLogo, Link, ThumbsUp, ChatText, Camera, Notepad
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Star Rating Component ── */
function StarRating({ rating, onRate, size = 20, interactive = false }) {
  return (
    <div className="flex gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          data-testid={`star-${i}`}
          onClick={() => interactive && onRate?.(i)}
          className={`${interactive ? "cursor-pointer hover:scale-110" : "cursor-default"} transition-transform`}
          disabled={!interactive}
        >
          <Star size={size} weight={i <= rating ? "fill" : "regular"} className={i <= rating ? "text-[#FFCC00]" : "text-[#E5E5E5]"} />
        </button>
      ))}
    </div>
  );
}

/* ── Progress Update Card ── */
function UpdateCard({ update }) {
  const typeIcons = { text: ChatText, photo: Camera, milestone: ThumbsUp };
  const Icon = typeIcons[update.update_type] || Notepad;
  return (
    <div data-testid={`update-${update.id}`} className="wp-card animate-slide-up">
      <div className="flex items-start gap-2 mb-2">
        <div className="w-7 h-7 rounded-sm bg-[#F4F4F5] flex items-center justify-center shrink-0">
          <Icon size={14} className="text-[#404040]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>
              {update.title || update.update_type.toUpperCase()}
            </h4>
            <span className="font-mono-data text-[10px] text-[#404040]">
              {new Date(update.created_at).toLocaleDateString()}
            </span>
          </div>
          {update.content && <p className="text-xs text-[#404040] mt-1">{update.content}</p>}
          {update.photo_base64 && (
            <img
              src={`data:image/jpeg;base64,${update.photo_base64}`}
              alt="Progress"
              className="mt-2 w-full h-40 object-cover rounded-sm border border-[#E5E5E5]"
            />
          )}
          {update.progress_percentage > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <Progress value={update.progress_percentage} className="flex-1 h-1" />
              <span className="font-mono-data text-[10px]">{update.progress_percentage}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Review Card ── */
function ReviewCard({ review }) {
  return (
    <div data-testid={`review-${review.id}`} className="wp-card animate-slide-up">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-bold">{review.customer_name || "Customer"}</h4>
          <span className="font-mono-data text-[10px] text-[#404040]">
            {new Date(review.created_at).toLocaleDateString()}
          </span>
        </div>
        <StarRating rating={review.rating} />
      </div>
      {review.review_text && <p className="text-xs text-[#404040] leading-relaxed">{review.review_text}</p>}
      {review.would_recommend && (
        <div className="flex items-center gap-1 mt-2 text-[10px] font-mono-data text-[#34C759]">
          <ThumbsUp size={10} /> WOULD RECOMMEND
        </div>
      )}
    </div>
  );
}

export default function CustomerPortalPage() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState("");
  const [createUpdateOpen, setCreateUpdateOpen] = useState(false);
  const [createReviewOpen, setCreateReviewOpen] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);

  // Form states
  const [updateForm, setUpdateForm] = useState({
    job_id: "", update_type: "text", title: "", content: "", photo_base64: "",
    progress_percentage: 0, visible_to_customer: true, created_by: ""
  });
  const [reviewForm, setReviewForm] = useState({
    job_id: "", customer_name: "", rating: 5, review_text: "", trade: "",
    would_recommend: true, share_to_google: false, share_to_yelp: false,
    share_to_bbb: false, share_to_nextdoor: false
  });
  const [customerForm, setCustomerForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [shareLinks, setShareLinks] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, custRes, updatesRes, reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/progress-updates`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/reviews/stats`),
      ]);
      setJobs(jobsRes.data);
      setCustomers(custRes.data);
      setUpdates(updatesRes.data);
      setReviews(reviewsRes.data);
      setReviewStats(statsRes.data);
    } catch {
      toast.error("Failed to load data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target.result.split(",")[1];
      setUpdateForm(f => ({ ...f, photo_base64: b64, update_type: "photo" }));
    };
    reader.readAsDataURL(file);
  };

  const createUpdate = async (e) => {
    e.preventDefault();
    if (!updateForm.job_id || !updateForm.title) {
      toast.error("Job and title required"); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/progress-updates`, updateForm);
      toast.success("Progress update sent");
      setCreateUpdateOpen(false);
      setUpdateForm({ job_id: "", update_type: "text", title: "", content: "", photo_base64: "", progress_percentage: 0, visible_to_customer: true, created_by: "" });
      fetchData();
    } catch { toast.error("Failed to create update"); }
    setSaving(false);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.job_id || !reviewForm.customer_name) {
      toast.error("Job and name required"); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/reviews`, reviewForm);
      if (res.data.share_links && Object.keys(res.data.share_links).length > 0) {
        setShareLinks(res.data.share_links);
      }
      toast.success("Review submitted! Thank you!");
      setCreateReviewOpen(false);
      setReviewForm({ job_id: "", customer_name: "", rating: 5, review_text: "", trade: "", would_recommend: true, share_to_google: false, share_to_yelp: false, share_to_bbb: false, share_to_nextdoor: false });
      fetchData();
    } catch { toast.error("Failed to submit review"); }
    setSaving(false);
  };

  const addCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/customers`, customerForm);
      toast.success("Customer added");
      setAddCustomerOpen(false);
      setCustomerForm({ name: "", email: "", phone: "", address: "" });
      fetchData();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const copyShareLink = (jobId) => {
    const link = `${BASE_URL}/customer/${jobId}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Link copied!"));
  };

  const filteredUpdates = selectedJob
    ? updates.filter(u => u.job_id === selectedJob)
    : updates;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <CircleNotch size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-3xl mx-auto" data-testid="customer-portal-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Customer Portal
          </h1>
          <p className="text-[10px] font-mono-data text-[#404040]">PROGRESS REPORTS & REVIEWS</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="add-customer-btn"
            onClick={() => setAddCustomerOpen(true)}
            className="px-2 py-1.5 text-[10px] font-bold border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] transition-colors"
          >
            + Customer
          </button>
          <button
            data-testid="send-update-btn"
            onClick={() => setCreateUpdateOpen(true)}
            className="px-2 py-1.5 text-[10px] font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C] transition-colors"
          >
            + Update
          </button>
        </div>
      </div>

      {/* Review Summary */}
      {reviewStats && reviewStats.total > 0 && (
        <div className="wp-card mb-4" data-testid="review-summary">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{reviewStats.avg_rating}</span>
                <StarRating rating={Math.round(reviewStats.avg_rating)} />
              </div>
              <span className="font-mono-data text-[10px] text-[#404040]">{reviewStats.total} REVIEWS | {reviewStats.recommend_pct}% RECOMMEND</span>
            </div>
            <div className="flex gap-1">
              {[5, 4, 3, 2, 1].map(r => (
                <div key={r} className="text-center">
                  <div className="w-4 bg-[#F4F4F5] rounded-sm overflow-hidden" style={{ height: 40 }}>
                    <div
                      className="bg-[#FFCC00] w-full transition-all"
                      style={{ height: `${reviewStats.total ? (reviewStats.distribution[r] / reviewStats.total) * 100 : 0}%`, marginTop: 'auto' }}
                    />
                  </div>
                  <span className="text-[8px] font-mono-data">{r}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="updates">
        <TabsList className="grid w-full grid-cols-3 h-9 mb-4">
          <TabsTrigger value="updates" className="text-xs" data-testid="portal-updates-tab">Updates</TabsTrigger>
          <TabsTrigger value="reviews" className="text-xs" data-testid="portal-reviews-tab">
            Reviews {reviews.length > 0 && `(${reviews.length})`}
          </TabsTrigger>
          <TabsTrigger value="share" className="text-xs" data-testid="portal-share-tab">Share Links</TabsTrigger>
        </TabsList>

        <TabsContent value="updates">
          {/* Job Filter */}
          <div className="mb-3">
            <Select value={selectedJob} onValueChange={setSelectedJob}>
              <SelectTrigger className="h-8 text-xs" data-testid="update-job-filter">
                <SelectValue placeholder="All Jobs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_jobs_filter">All Jobs</SelectItem>
                {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {filteredUpdates.length === 0 ? (
            <div className="wp-card text-center py-8 text-sm text-[#404040]">
              No progress updates yet. Send one to keep customers informed.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUpdates.map((u, i) => (
                <div key={u.id} style={{ animationDelay: `${i * 0.05}s` }}>
                  <UpdateCard update={u} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reviews">
          <div className="mb-3 flex justify-end">
            <button
              data-testid="request-review-btn"
              onClick={() => setCreateReviewOpen(true)}
              className="px-3 py-1.5 text-xs font-bold bg-[#FFCC00] text-[#0A0A0A] rounded-sm hover:bg-[#E6B800] transition-colors flex items-center gap-1"
            >
              <Star size={12} /> New Review
            </button>
          </div>
          {reviews.length === 0 ? (
            <div className="wp-card text-center py-8 text-sm text-[#404040]">
              No reviews yet. Request one from a satisfied customer.
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.map((r, i) => (
                <div key={r.id} style={{ animationDelay: `${i * 0.05}s` }}>
                  <ReviewCard review={r} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="share">
          <p className="text-xs text-[#404040] mb-3">Share a job's progress report link with customers so they can view live updates.</p>
          {jobs.length === 0 ? (
            <div className="wp-card text-center py-8 text-sm text-[#404040]">No jobs to share.</div>
          ) : (
            <div className="space-y-2">
              {jobs.map(job => (
                <div key={job.id} className="wp-card flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{job.title}</p>
                    <span className="font-mono-data text-[10px] text-[#404040]">{job.address}</span>
                  </div>
                  <button
                    data-testid={`copy-link-${job.id}`}
                    onClick={() => copyShareLink(job.id)}
                    className="shrink-0 px-2 py-1.5 text-[10px] font-bold border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] transition-colors flex items-center gap-1"
                  >
                    <Link size={10} /> Copy Link
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Share Links Modal */}
      {shareLinks && (
        <Dialog open={!!shareLinks} onOpenChange={() => setShareLinks(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Share Your Review</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-[#404040] mb-3">Help us by sharing your experience on these platforms:</p>
            <div className="space-y-2">
              {Object.entries(shareLinks).map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-link-${platform}`}
                  className="wp-card flex items-center gap-2 hover:border-[#0A0A0A] transition-colors"
                >
                  <ShareNetwork size={16} className="text-[#002FA7]" />
                  <span className="text-sm font-medium capitalize">{platform}</span>
                  <span className="text-xs text-[#404040] ml-auto">Open &rarr;</span>
                </a>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Update Dialog */}
      <Dialog open={createUpdateOpen} onOpenChange={setCreateUpdateOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Send Progress Update</DialogTitle>
          </DialogHeader>
          <form onSubmit={createUpdate} className="space-y-3">
            <Select value={updateForm.job_id} onValueChange={v => setUpdateForm(f => ({...f, job_id: v}))}>
              <SelectTrigger data-testid="update-job-select"><SelectValue placeholder="Select Job" /></SelectTrigger>
              <SelectContent>
                {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <input
              data-testid="update-title-input"
              type="text" placeholder="Update title"
              value={updateForm.title} onChange={e => setUpdateForm(f => ({...f, title: e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <Select value={updateForm.update_type} onValueChange={v => setUpdateForm(f => ({...f, update_type: v}))}>
              <SelectTrigger data-testid="update-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text Update</SelectItem>
                <SelectItem value="photo">Photo Update</SelectItem>
                <SelectItem value="milestone">Milestone</SelectItem>
              </SelectContent>
            </Select>
            <textarea
              data-testid="update-content-input"
              placeholder="Describe the progress..."
              value={updateForm.content} onChange={e => setUpdateForm(f => ({...f, content: e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-20"
            />
            <div>
              <label className="text-[10px] font-mono-data text-[#404040] block mb-1">ATTACH PHOTO</label>
              <input
                data-testid="update-photo-input"
                type="file" accept="image/*" onChange={handlePhotoUpload}
                className="w-full text-xs file:mr-2 file:py-1 file:px-3 file:rounded-sm file:border file:border-[#E5E5E5] file:text-xs file:font-bold file:bg-white hover:file:border-[#0A0A0A]"
              />
              {updateForm.photo_base64 && (
                <img src={`data:image/jpeg;base64,${updateForm.photo_base64}`} alt="Preview" className="mt-2 h-24 rounded-sm border border-[#E5E5E5] object-cover" />
              )}
            </div>
            <div>
              <label className="text-[10px] font-mono-data text-[#404040] block mb-1">PROGRESS %</label>
              <input
                data-testid="update-progress-input"
                type="range" min="0" max="100" value={updateForm.progress_percentage}
                onChange={e => setUpdateForm(f => ({...f, progress_percentage: parseInt(e.target.value)}))}
                className="w-full"
              />
              <span className="font-mono-data text-xs">{updateForm.progress_percentage}%</span>
            </div>
            <button
              data-testid="submit-update-btn"
              type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
            >
              <PaperPlaneTilt size={14} /> {saving ? "Sending..." : "Send Update"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Review Dialog */}
      <Dialog open={createReviewOpen} onOpenChange={setCreateReviewOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Leave a Review</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReview} className="space-y-3">
            <Select value={reviewForm.job_id} onValueChange={v => setReviewForm(f => ({...f, job_id: v}))}>
              <SelectTrigger data-testid="review-job-select"><SelectValue placeholder="Select Job" /></SelectTrigger>
              <SelectContent>
                {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <input
              data-testid="review-name-input"
              type="text" placeholder="Your name"
              value={reviewForm.customer_name} onChange={e => setReviewForm(f => ({...f, customer_name: e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <div>
              <label className="text-[10px] font-mono-data text-[#404040] block mb-2">RATING</label>
              <StarRating
                rating={reviewForm.rating}
                onRate={r => setReviewForm(f => ({...f, rating: r}))}
                size={28}
                interactive
              />
            </div>
            <textarea
              data-testid="review-text-input"
              placeholder="Share your experience..."
              value={reviewForm.review_text} onChange={e => setReviewForm(f => ({...f, review_text: e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-20"
            />
            <div className="flex items-center gap-2">
              <input
                data-testid="review-recommend"
                type="checkbox" checked={reviewForm.would_recommend}
                onChange={e => setReviewForm(f => ({...f, would_recommend: e.target.checked}))}
                className="rounded-sm"
              />
              <label className="text-xs">I would recommend this company</label>
            </div>
            <div>
              <label className="text-[10px] font-mono-data text-[#404040] block mb-2">SHARE TO REVIEW SITES</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "share_to_google", label: "Google Reviews" },
                  { key: "share_to_yelp", label: "Yelp" },
                  { key: "share_to_bbb", label: "BBB" },
                  { key: "share_to_nextdoor", label: "Nextdoor" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input
                      data-testid={`review-${key}`}
                      type="checkbox"
                      checked={reviewForm[key]}
                      onChange={e => setReviewForm(f => ({...f, [key]: e.target.checked}))}
                      className="rounded-sm"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <button
              data-testid="submit-review-btn"
              type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#FFCC00] text-[#0A0A0A] text-sm font-bold rounded-sm hover:bg-[#E6B800] transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
            >
              <Star size={14} weight="fill" /> {saving ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerOpen} onOpenChange={setAddCustomerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Add Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={addCustomer} className="space-y-3">
            <input
              data-testid="customer-name-input"
              type="text" placeholder="Customer name"
              value={customerForm.name} onChange={e => setCustomerForm(f => ({...f, name: e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <input
              data-testid="customer-email-input"
              type="email" placeholder="Email"
              value={customerForm.email} onChange={e => setCustomerForm(f => ({...f, email: e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <input
              data-testid="customer-phone-input"
              type="tel" placeholder="Phone"
              value={customerForm.phone} onChange={e => setCustomerForm(f => ({...f, phone: e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <input
              data-testid="customer-address-input"
              type="text" placeholder="Address"
              value={customerForm.address} onChange={e => setCustomerForm(f => ({...f, address: e.target.value}))}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <button
              data-testid="create-customer-submit"
              type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-50"
            >
              {saving ? "Adding..." : "Add Customer"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
